// Single source of truth for case-ticket labels/options — every caseTicket
// component imports from here instead of keeping its own copy.

export const ISSUE_TYPE_LABELS = {
  damaged: 'สินค้าเสียหาย',
  incomplete: 'ของมีปัญหา/ของไม่ครบ',
  wrong_item: 'ส่งผิด',
  tracking_follow: 'ลูกค้าตามของ',
  // set by the server when every chased parcel is send-only: no Japan check
  sendonly_follow: 'ลูกค้าตามของ (ฝากส่ง)',
  system_bug: 'บั๊กระบบ/โปรแกรม',
  feature_request: 'ขอฟีเจอร์ใหม่',
  question: 'คำถามภายใน',
  other: 'อื่นๆ',
};

// Options offered by the create form per ticket type. Customer list is limited
// to these for now — more (ส่งผิด, อื่นๆ) can be added back later; the backend
// already accepts them. ตามของ runs its own lane (round + items, born at the
// warehouse hand-off).
export const CUSTOMER_ISSUE_TYPE_OPTIONS = [
  { value: 'damaged', label: ISSUE_TYPE_LABELS.damaged },
  { value: 'incomplete', label: ISSUE_TYPE_LABELS.incomplete },
  { value: 'tracking_follow', label: ISSUE_TYPE_LABELS.tracking_follow },
];

export const INTERNAL_ISSUE_TYPE_OPTIONS = [
  { value: 'system_bug', label: ISSUE_TYPE_LABELS.system_bug },
  { value: 'feature_request', label: ISSUE_TYPE_LABELS.feature_request },
  { value: 'question', label: ISSUE_TYPE_LABELS.question },
  { value: 'other', label: ISSUE_TYPE_LABELS.other },
];

export const STATUS_LABELS = {
  open: 'เปิดเคส',
  waiting_supervisor: 'รอหัวหน้าตอบ',
  need_info: 'รอข้อมูลเพิ่ม',
  waiting_customer: 'รอลูกค้าตอบ',
  in_progress: 'อยู่ระหว่างดำเนินการ',
  customer_update: 'รอแอดมินแจ้งลูกค้า',
  customer_informed: 'แจ้งลูกค้าแล้ว',
  customer_confirmed: 'ลูกค้ายืนยันแล้ว',
  waiting_warehouse: 'รอ Kluay เช็ค',
  closed: 'ปิดเคส',
  // pseudo history entry (never a real ticket.status) — renders in the 📜 log
  extend_followup: '⏰ ต่อเวลาติดตาม +2 วัน',
};

export const STATUS_BADGE_STYLES = {
  open: 'bg-gray-100 text-gray-700',
  waiting_warehouse: 'bg-orange-100 text-orange-700',
  waiting_supervisor: 'bg-blue-100 text-blue-700',
  need_info: 'bg-orange-100 text-orange-700',
  waiting_customer: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-indigo-100 text-indigo-700',
  customer_update: 'bg-purple-100 text-purple-700',
  customer_informed: 'bg-sky-100 text-sky-700',
  customer_confirmed: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-green-100 text-green-700',
};

// need_info / waiting_customer are DETOURS from waiting_supervisor (supervisor
// asked → admin asks customer → answer flips it back) — the stepper shows both
// at the same position; the status badge tells the real story.
export const STEPPER_STATUS_ALIAS = { need_info: 'waiting_supervisor', waiting_customer: 'waiting_supervisor' };

// Full customer lifecycle, in stepper order. Internal tickets use the subset
// without the two customer stages.
export const CUSTOMER_STEPS = ['open', 'waiting_supervisor', 'in_progress', 'customer_update', 'customer_informed', 'customer_confirmed', 'closed']
  .map((key) => ({ key, label: STATUS_LABELS[key] }));

export const INTERNAL_STEPS = ['open', 'waiting_supervisor', 'in_progress', 'closed']
  .map((key) => ({ key, label: STATUS_LABELS[key] }));

// ตามของ: born at the warehouse hand-off, then the supervisor's decision, the
// admin's message, waiting for the ship, found / refunded.
export const TRACKING_STEPS = ['open', 'waiting_warehouse', 'waiting_supervisor', 'in_progress', 'customer_update', 'customer_informed', 'closed']
  .map((key) => ({ key, label: STATUS_LABELS[key] }));

// The quick check's four answers (services/caseTicket/diagnosis.js) — what the
// admin sees per parcel. Only in_system is vouched for: it means the sheet
// IMPORT linked the order. Anything else is "not confirmed" — and the import
// can lag the sheet by a day or two, so not_in_system is never read as lost.
export const DIAGNOSIS_META = {
  in_system:        { label: '📦 มีรอบเรือ/แอร์แล้ว', cls: 'bg-green-100 text-green-800', hint: 'ระบบผูกกับชีทโกดังแล้ว — ของกำลังมาตามรอบ' },
  not_in_system:    { label: '🔍 ยังไม่เจอในระบบ', cls: 'bg-orange-100 text-orange-800', hint: 'มีเลขพัสดุ แต่ยังไม่ผูกกับชีท — ชีทอาจยังไม่ได้อัปโหลด (1–2 วัน) หรือของไม่ถึงโกดัง' },
  no_tracking:      { label: '🚫 ไม่มีเลข', cls: 'bg-red-100 text-red-800', hint: 'ส่งแบบไม่มีเลข — รู้ผลตอนแกะกล่องที่ไทย' },
  tracking_missing: { label: '❓ ยังไม่มีเลขในระบบ', cls: 'bg-gray-100 text-gray-700', hint: 'ไปหาเลขพัสดุมาใส่ก่อน' },
  order_not_found:  { label: 'ไม่พบออเดอร์', cls: 'bg-gray-100 text-gray-700', hint: '' },
};
export const RESOLVED_ON_THE_SPOT = ['in_system'];
// The warehouse can only be asked about a parcel we have looked up. A blank
// tracking is the ADMIN's job first — find the number, or confirm there is none.
export const NEEDS_ADMIN_FIRST = ['tracking_missing'];

export const FOUND_STATUS_LABELS = {
  pending: 'กำลังตามหา',
  found: 'เจอแล้ว',
  not_found: 'หาไม่เจอ',
  // Settled at ✓ แจ้งลูกค้าแล้ว: a refund was CREATED (not yet transferred) or,
  // with nothing paid, the order was only cancelled — the line under it says which.
  refunded: 'ปิดแล้ว (คืนเงิน/ยกเลิก)',
};

export const SHIPPING_CHANNEL_LABELS = {
  general_transport: 'ขนส่งทั่วไป',
  lalamove: 'Lalamove',
  warehouse_pickup: 'เข้ารับที่โกดัง',
};

export const SHIPPING_CHANNEL_OPTIONS = Object.entries(SHIPPING_CHANNEL_LABELS)
  .map(([value, label]) => ({ value, label }));

// Same list used on the customer profile's "กลุ่มสินค้า" field, plus อุปกรณ์ตกปลา
// added on top for case reporting.
export const PRODUCT_GROUPS = [
  'ไม้กอล์ฟ', 'อะไหล่รถยนต์', 'เครื่องดนตรี', 'ฟิกเกอร์/ของเล่น', 'การ์ด',
  'ของใช้ทั่วไป', 'เสื้อผ้า/รองเท้า', 'ของกิน', 'อุปกรณ์ตกปลา', 'ฝากส่ง', 'อื่นๆ',
];

// Case issueType -> the closest matching reason on the /refund page, so the
// hand-off doesn't leave the manager guessing which reason to pick.
export const ISSUE_TYPE_TO_REFUND_REASON = {
  damaged: 'damaged',
  incomplete: 'other',
  wrong_item: 'other',
  tracking_follow: 'other',
  sendonly_follow: 'other',
  other: 'other',
};

// Internal tickets carry their own topic; customer tickets show the linked profile.
export const ticketDisplayName = (ticket) =>
  ticket?.title || ticket?.customer?.contact || ticket?.customer?.smileName || `เคส #${ticket?.id}`;

// Local-time YYYY-MM-DD — toISOString() would shift a picked date back one day
// in UTC+7 (17:00Z of the previous day), off-by-one in every report grouping.
export const toDateOnlyString = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
